import { closePanel } from "@/layouts/layouts";
import { computed, ref, Ref } from "vue";
import Bus from "@/core/bus";
import { toastError } from "@/helpers/toastify";

const PROPS_ERROR = "Не заполнены необходимые поля";

export interface ControllerProps {
  store: Record<string, any>;
  config: Record<string, any>;
  data: Record<string, any> | Ref<Record<string, any>>;
  params: Record<string, any>;
  panel?: Record<string, any>;
}

// Класс-контроллер для управления формой редактирования (кнопки, сохранение, валидация, доступы)
export default class EditController {
  // readonly — вычисляется из props.store.state.readonly
  public readonly readonly: Ref<boolean>;
  // Ссылки на store и data (реактивные)
  protected _store?: Ref<any>;
  protected _data?: Ref<any>;

  // Массив кнопок для панели
  public readonly panelFun = ref<any[]>([]);
  // Стандартные команды для панели (сохранить, закрыть и т.д.)
  public readonly standardCommands: Record<string, any>;

  constructor(protected form: Ref<any>, protected props: ControllerProps) {
    this.readonly = computed(() => props.store.state.readonly);

    this.standardCommands = {
      saveClose: {
        caption: "Сохранить и закрыть",
        title: "Сохранить и закрыть",
        icon: <i class="icon icon-check-circle"></i>,
        class: "btn btn-save",
        onClick: async () => {
          const result = await this.save();
          if (result) {
            this.props.panel?.close?.();
          }
        },
      },
      save: {
        caption: "Сохранить",
        title: "Сохранить",
        icon: <i class="icon icon-save"></i>,
        class: "btn btn-save",
        onClick: async () => await this.save(),
      },
      close: {
        caption: "Закрыть",
        title: "Закрыть",
        class: "btn btn-close",
        onClick: () => {
          if (
            this.props.panel &&
            (this.props.panel as any)._processingReceipt === true
          ) {
            return;
          }
          closePanel(this.props.panel);
        },
      },
    };

    const panelFun = [];

    if (
      !this.readonly.value &&
      (props.store.model.access.update || props.store.model.access.create)
    ) {
      panelFun.push({ saveClose: {}, save: {} });
    }

    panelFun.push({ close: {} });

    this.createPanelFun(panelFun);

    // Защита метода save от переопределения
    Object.defineProperty(this, "save", {
      value: this.save.bind(this),
      writable: false,
      configurable: false,
    });
  }

  // Геттер для store
  get store(): Ref<any> {
    if (!this._store) {
      this._store = computed(() => this.form?.value?.store ?? null);
    }
    return this._store;
  }

  // Геттер для data
  get data(): Ref<any> {
    if (!this._data) {
      this._data = computed(() => this.form?.value?.data ?? {});
    }
    return this._data;
  }

  // Создать массив кнопок для панели
  public createPanelFun(items: any[]) {
    this.panelFun.value.splice(0, this.panelFun.value.length);

    for (const item of items) {
      const panel: any = {};

      for (const key in item) {
        panel[key] = {};

        if (key in this.standardCommands) {
          Object.assign(panel[key], this.standardCommands[key], item[key]);
        } else {
          Object.assign(panel[key], item[key]);
        }
      }

      this.panelFun.value.push(panel);
    }
  }

  // Установить кнопки для панели (альтернативный способ)
  public setButtons() {
    const buttons: any[] = [];

    if (!this.readonly.value) {
      buttons.push({
        caption: "Сохранить",
        class: "btn-action",
        onClick: async () => await this.save(),
      });
    }

    this.props.panel?.setButtons?.(buttons);
  }

  // Метод сохранения (save) — защищён от переопределения
  protected async save(noValidation = false): Promise<boolean> {
    try {
      if (!this.store?.value) {
        console.warn("Store is not initialized yet", {
          form: this.form?.value,
          store: this.store,
        });
        return false;
      }

      // Вызов beforeSave!
      if (!(await this.beforeSave())) {
        return false;
      }

      // Сначала вызываем базовую валидацию
      if (!noValidation) {
        await this.validationBase();
      }

      const saveResult = await this.form.value?.save?.();

      const storeValue = this.store.value;
      if (storeValue?.owner && storeValue?.name) {
        Bus.$emit("save-data-child", {
          table: storeValue.name,
          newrecord: false,
        });
      }

      if (saveResult && typeof (this as any).afterSave === "function") {
        await (this as any).afterSave();
      }

      return saveResult;
    } catch (error) {
      this.handleError(error);
      return false;
    }
  }

  // Метод, который вызывается перед сохранением (можно переопределять)
  protected async beforeSave(): Promise<boolean> {
    return true;
  }

  // Публичный метод для вызова приватного save
  public async triggerSave(noValidation = false): Promise<boolean> {
    return this.save(noValidation);
  }

  // Базовая валидация для UI
  protected async validationBase(): Promise<void> {
    // Сначала вызываем кастомную валидацию
    await this.validate();
    // Потом вызываем валидацию формы для UI
    await this.form.value?.validation?.();
  }

  // Кастомная валидация (можно переопределять)
  protected async validate(): Promise<boolean> {
    return true;
  }

  // Обработка ошибок при сохранении
  protected handleError(error: any): void {
    console.error("Ошибка при сохранении:", error);
    toastError("Ошибка при сохранении данных.");
  }

  // Получить настройки формы
  public settings() {
    return Object.assign({}, this.props.config);
  }

  // Получить набор данных для setup() компонента
  public setup() {
    return {
      readonly: this.readonly,
      controller: this,
      form: this.form,
      settings: this.settings(),
    };
  }
}
